import { useCallback, useEffect, useRef, useState, type CSSProperties } from 'react';
import { createPortal } from 'react-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Badge } from '@/components/ui/Badge/Badge';
import { CloseIcon, ChevronLeftIcon } from '@/components/ui/Icons';
import { OverlayScrollArea } from '@/components/ui/OverlayScrollArea/OverlayScrollArea';
import { useOverlayAnimation } from '@/hooks/useOverlayAnimation';
import { useFloatingPanelDrag } from '@/hooks/useFloatingPanelDrag';
import { api, type MiniAppCatalogRow, ApiError } from '@/api/client';
import { getMiniAppComponent } from '@/mini-apps/miniAppRegistry';
import {
  MINI_APPS_CATALOG_QUERY_KEY,
  replaceMiniAppCatalogRow,
  toggleMiniAppCatalogLikeOptimistically,
} from '@/lib/miniAppsCatalogQuery';
import { MiniAppsShowcase } from './MiniAppsShowcase';
import './MiniAppFloatingPanel.css';

const PANEL_MAX_WIDTH = '28rem';
const EMPTY_APPS: MiniAppCatalogRow[] = [];

export interface MiniAppFloatingPanelProps {
  open: boolean;
  onClose: () => void;
}

type ViewMode = 'showcase' | 'app';

export function MiniAppFloatingPanel({ open, onClose }: MiniAppFloatingPanelProps) {
  const { shouldRender, contentClasses, requestClose } = useOverlayAnimation(open, onClose, true);
  const queryClient = useQueryClient();
  const { shellRef, position, headerDragProps } = useFloatingPanelDrag({
    enabled: open,
    initialPosition: 'topLeft',
    draggingHeaderClassName: 'mini-app-floating-panel__header_dragging',
  });
  const pendingLikeIdsRef = useRef(new Set<string>());
  const [viewMode, setViewMode] = useState<ViewMode>('showcase');
  const [selectedAppId, setSelectedAppId] = useState<string | null>(null);
  const [pendingLikeIds, setPendingLikeIds] = useState<ReadonlySet<string>>(() => new Set());

  const catalogQuery = useQuery({
    queryKey: MINI_APPS_CATALOG_QUERY_KEY,
    queryFn: () => api.miniApps.list(),
    enabled: open && shouldRender,
  });

  const appsSorted = catalogQuery.data ?? EMPTY_APPS;
  const selectedMeta = selectedAppId ? appsSorted.find((app) => app.id === selectedAppId) : undefined;

  const setLikePending = useCallback((id: string, pending: boolean) => {
    const nextIds = new Set(pendingLikeIdsRef.current);
    if (pending) {
      nextIds.add(id);
    } else {
      nextIds.delete(id);
    }
    pendingLikeIdsRef.current = nextIds;
    setPendingLikeIds(nextIds);
  }, []);

  useEffect(() => {
    if (!open) return;
    setViewMode('showcase');
    setSelectedAppId(null);
  }, [open]);

  useEffect(() => {
    if (!shouldRender) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') requestClose();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [shouldRender, requestClose]);

  const handleOpenApp = useCallback((id: string) => {
    setSelectedAppId(id);
    setViewMode('app');
  }, []);

  const handleBackToShowcase = useCallback(() => {
    setViewMode('showcase');
    setSelectedAppId(null);
  }, []);

  const handleToggleLike = useCallback(
    (id: string) => {
      if (pendingLikeIdsRef.current.has(id)) return;

      const catalog = queryClient.getQueryData<MiniAppCatalogRow[]>(MINI_APPS_CATALOG_QUERY_KEY);
      const prevRow = catalog?.find((app) => app.id === id);
      if (!prevRow) return;

      const nextLiked = !prevRow.liked_by_me;
      setLikePending(id, true);

      queryClient.setQueryData<MiniAppCatalogRow[] | undefined>(MINI_APPS_CATALOG_QUERY_KEY, (prev) =>
        toggleMiniAppCatalogLikeOptimistically(prev, id, nextLiked)
      );

      void (async () => {
        try {
          const row = nextLiked ? await api.miniApps.like(id) : await api.miniApps.unlike(id);
          queryClient.setQueryData<MiniAppCatalogRow[] | undefined>(MINI_APPS_CATALOG_QUERY_KEY, (prev) =>
            replaceMiniAppCatalogRow(prev, row)
          );
        } catch {
          queryClient.setQueryData<MiniAppCatalogRow[] | undefined>(MINI_APPS_CATALOG_QUERY_KEY, (prev) =>
            replaceMiniAppCatalogRow(prev, prevRow)
          );
        } finally {
          setLikePending(id, false);
        }
      })();
    },
    [queryClient, setLikePending]
  );

  if (!shouldRender) return null;

  const panelStyle = {
    left: position.left,
    top: position.top,
    '--mini-app-floating-panel-max-width': PANEL_MAX_WIDTH,
  } as CSSProperties;

  const AppBody = selectedAppId ? getMiniAppComponent(selectedAppId) : null;

  const dialogAriaLabel =
    viewMode === 'showcase'
      ? 'Мини-приложения, витрина'
      : `Мини-приложения: ${selectedMeta?.name ?? selectedAppId ?? 'приложение'}`;

  const panel = (
    <div
      ref={shellRef}
      className={`mini-app-floating-panel ${contentClasses}`}
      style={panelStyle}
      role="dialog"
      aria-label={dialogAriaLabel}
    >
      <div className="mini-app-floating-panel__header" {...headerDragProps}>
        <span className="mini-app-floating-panel__title">
          {viewMode === 'app' ? (
            <button
              type="button"
              className="mini-app-floating-panel__back"
              aria-label="Назад к витрине"
              onClick={(ev) => {
                ev.stopPropagation();
                handleBackToShowcase();
              }}
            >
              <ChevronLeftIcon size={20} aria-hidden />
            </button>
          ) : null}
          <Badge variant="reader">MINI-APP</Badge>
          <span className="mini-app-floating-panel__title-text">Мини-приложения</span>
        </span>
        <div className="mini-app-floating-panel__header-actions">
          <button
            type="button"
            className="mini-app-floating-panel__icon-action mini-app-floating-panel__icon-action_danger"
            aria-label="Закрыть"
            onClick={(ev) => {
              ev.stopPropagation();
              requestClose();
            }}
          >
            <CloseIcon size={18} aria-hidden />
          </button>
        </div>
      </div>
      <div className="mini-app-floating-panel__body">
        <OverlayScrollArea className="mini-app-floating-panel__body-scroll">
          {viewMode === 'showcase' ? (
            <MiniAppsShowcase
              apps={appsSorted}
              loading={catalogQuery.isLoading}
              errorText={
                catalogQuery.error
                  ? catalogQuery.error instanceof ApiError
                    ? catalogQuery.error.message
                    : (catalogQuery.error as Error).message
                  : null
              }
              onOpenApp={handleOpenApp}
              onToggleLike={handleToggleLike}
              pendingLikeIds={pendingLikeIds}
            />
          ) : AppBody ? (
            <AppBody />
          ) : (
            <div className="mini-app-floating-panel__status">
              <p className="mini-app-floating-panel__error" role="alert">
                Компонент для этого мини-приложения не подключён в реестре.
              </p>
            </div>
          )}
        </OverlayScrollArea>
      </div>
    </div>
  );

  return createPortal(panel, document.body);
}
